import {DataPropertyProfileMap} from "@/store/modules/hcs/tradeContract/model";
import TradeContractView from "@/components/pages/contract/TradeContract/TradeContractView";
import DataPropertyProfileLinkManager, {
  ProducerAndConsumer,
  SelfAndOther
} from "@/components/pages/contract/TradeContract/AdaptContract/DataPropertyProfileLinkManager";

/**
 * データ項目プロファイルコネクタ同士を疎に結合するリンク
 */
export class DataPropertyProfileLink implements DataPropertyProfileMap {
  /** TradeContractView を取得する function */
  private readonly tcvProducer: () => TradeContractView;

  /** DataPropertyProfileLinkManager */
  private readonly manager: DataPropertyProfileLinkManager;

  /** 提供側と利用側のデータ項目プロファイルID */
  private key: ProducerAndConsumer;

  /**
   * 型変換メソッド
   *
   * データ型が異なる場合に、変換のための文字列を設定する。
   * 文字列の文法は双方のシステムに依存するのでここでは定めない。
   */
  public typeConversionMethod: string;

  /**
   * コンストラクタ
   *
   * @param tcvProducer TradeContractView を取得する function
   * @param manager DataPropertyProfileLinkManager
   * @param key 「提供側と利用側」または「自分側と相手側」のデータ項目プロファイルID
   */
  constructor(tcvProducer: () => TradeContractView, manager: DataPropertyProfileLinkManager, key: ProducerAndConsumer | SelfAndOther) {
    this.tcvProducer = tcvProducer;
    this.manager = manager;
    this.typeConversionMethod = 'auto';
    if('selfDataPropertyProfileId' in key && 'otherDataPropertyProfileId' in key) {
      if(this.tradeContractView.isSelfConsume) {
        this.key = {
          consumerDataPropertyProfileId: key.selfDataPropertyProfileId,
          producerDataPropertyProfileId: key.otherDataPropertyProfileId,
        };
      } else {
        this.key = {
          consumerDataPropertyProfileId: key.otherDataPropertyProfileId,
          producerDataPropertyProfileId: key.selfDataPropertyProfileId,
        };
      }
    } else if('producerDataPropertyProfileId' in key && 'consumerDataPropertyProfileId' in key) {
      this.key = key;
    } else {
      throw new Error('Unknown key type: ' + JSON.stringify(key));
    }
  }

  /**
   * TradeContractView を取得します
   * @private
   */
  private get tradeContractView() {
    return this.tcvProducer();
  }

  /**
   * 提供側のデータ項目プロファイルID
   */
  get producerDataPropertyProfileId() {
    return this.key.producerDataPropertyProfileId;
  }

  /**
   * 利用側のデータ項目プロファイルID
   */
  get consumerDataPropertyProfileId() {
    return this.key.consumerDataPropertyProfileId;
  }

  /**
   * 自分側のデータ項目プロファイルID
   */
  get selfDataPropertyProfileId() {
    if(this.tradeContractView.isSelfConsume) {
      return this.key.consumerDataPropertyProfileId;
    } else {
      return this.key.producerDataPropertyProfileId;
    }
  }

  /**
   * 相手側のデータ項目プロファイルID
   */
  get otherDataPropertyProfileId() {
    if(this.tradeContractView.isOtherConsume) {
      return this.key.consumerDataPropertyProfileId;
    } else {
      return this.key.producerDataPropertyProfileId;
    }
  }

  /**
   * 省略値
   */
  get defaultValue() {
    if(this.consumerDataPropertyProfileId !== null) {
      const connector = this.manager.getConnector(this.consumerDataPropertyProfileId);
      if(connector?.defaultValue) {
        return connector.defaultValue;
      }
    }

    if(this.producerDataPropertyProfileId !== null) {
      const connector = this.manager.getConnector(this.producerDataPropertyProfileId);
      if(connector?.defaultValue) {
        return connector.defaultValue;
      }
    }

    return undefined;
  }

  /**
   * オブジェクトに変換します
   */
  toObject(): DataPropertyProfileMap {
    return {
      producerDataPropertyProfileId: this.producerDataPropertyProfileId,
      consumerDataPropertyProfileId: this.consumerDataPropertyProfileId,
      typeConversionMethod: this.typeConversionMethod,
      defaultValue: this.defaultValue
    };
  }
}
